import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { BaseStitchListComponent, type StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { MessageComponent } from '@modules/elements/components/message/message.component';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { MailAppStateService } from '@modules/messages/services/mail-app-state.service';
import { MailReaderOpenService } from '@modules/messages/services/mail-reader-open.service';
import { MailboxTenantScopeService } from '@modules/common/services/mailbox-tenant-scope.service';
import { MessagesService } from '@modules/messages/services/messages.service';
import { MessagesFilters } from '@modules/messages/types/messages-filters';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { MessageState } from '@sharedlib/common/types/mail-app/MessageState';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { MessageSummaryDto } from '@sharedlib/dtos/dto/mail-app/message.dto';
import { BehaviorSubject, EMPTY, merge, Observable } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-messages2-list',
  templateUrl: './messages-list.component.html',
  styleUrl: './messages-list.component.less',
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class MessagesListComponent
  extends BaseStitchListComponent<MessageSummaryDto>
  implements OnDestroy, OnChanges
{
  /** Show per-row folder/status-location badges (search results, where results span folders). */
  public readonly showLocationBadge = input<boolean>(false);
  public readonly tenantItems = input<ReadonlyArray<{ readonly id?: string; readonly accountId?: string }>>();

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Show each row's archive and trash quick actions; Reply and Forward follow `actionsButtonEnabled` alone. */
  public readonly archiveTrashEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge hub asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The hub draws its related items as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  /**
   * The last page the server answered stopped short of every match: a Team Inbox member's search past its
   * 10,000-candidate ceiling. Only a team login is ever told (the server never marks a personal login's list).
   */
  public readonly truncated = signal(false);
  protected readonly accountKind = inject(AccountKindService);

  public isPendingReview(item: MessageSummaryDto): boolean {
    return 'pendingReview' in item && item.pendingReview === true;
  }

  public isPendingAssignment(item: MessageSummaryDto): boolean {
    return 'pendingAssignment' in item && item.pendingAssignment === true;
  }

  public items: StitchListItem<MessageSummaryDto>[] = [];

  @Input()
  public override filters: Partial<MessagesFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public withConnections = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickMessage = new EventEmitter<MessageSummaryDto>();
  @Output()
  public readonly clickThreadMessage = new EventEmitter<MessageSummaryDto>();
  @Output()
  public readonly doubleClickThreadMessage = new EventEmitter<MessageSummaryDto>();
  @Output()
  public readonly openMessage = new EventEmitter<MessageSummaryDto>();
  @Output()
  public readonly doubleClickMessage = new EventEmitter<MessageSummaryDto>();
  /** The message a click is about to open (see {@link handleOpenPending}), `null` once that open is called off. */
  @Output()
  public readonly pendingMessage = new EventEmitter<MessageSummaryDto | null>();

  @ViewChildren(MessageComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  protected effectiveThreadedView = false;

  protected override getStitchType(): StitchType {
    return StitchType.MESSAGE;
  }

  private readonly messagesService = inject(MessagesService);
  private readonly mailAppStateService = inject(MailAppStateService);
  private readonly linkedInfoService = inject(LinkedInfoService);
  private readonly injector = inject(Injector);
  private readonly mailboxTenantScopeService = inject(MailboxTenantScopeService, { optional: true });
  /** Optional: the list is also drawn outside Mail's app context, where nothing opens the reader from search. */
  private readonly readerOpen = inject(MailReaderOpenService, { optional: true });

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    const spaceIds = new Set<string>([this.spaceContextStore.contextId()]);
    const effectiveTenantId = this.mailboxTenantScopeService?.effectiveTenantId();
    if (effectiveTenantId) {
      spaceIds.add(effectiveTenantId);
    }
    for (const { accountId } of this.tenantItems() ?? []) {
      if (accountId) {
        spaceIds.add(accountId);
      }
    }
    return [...spaceIds];
  }

  constructor() {
    super();

    merge(
      this.mailAppStateService.getRefreshAll(),
      this.linkedInfoService.getRefresh(),
      this.messagesService.getRefresh(),
      // we usually don't reload the message list when a message is flagged
      // as it remains in the list, however, if we're in the "flagged" folder,
      // we'd like it to be updated
      this.mailAppStateService.getVirtualFolder().pipe(
        switchMap((folder) =>
          folder === 'flagged'
            ? this.stitchEventsService.getEvents(() => this.stitchSignalSpaceIds(), StitchType.MESSAGE, [
                SignalStitchEventType.FLAGGED,
                SignalStitchEventType.UNFLAGGED,
              ])
            : EMPTY,
        ),
      ),
      // Refresh the scheduled folder when a message is scheduled or unscheduled.
      // Post-filter on scheduling fields to avoid reloading on unrelated message updates.
      this.mailAppStateService.getVirtualFolder().pipe(
        switchMap((folder) =>
          folder === 'scheduled'
            ? this.stitchEventsService
                .getEvents(() => this.stitchSignalSpaceIds(), StitchType.MESSAGE, SignalStitchEventType.UPDATED)
                .pipe(filter((event) => 'state' in event.changes || 'scheduledAt' in event.changes))
            : EMPTY,
        ),
      ),
      // the VIP folder is the one mail folder whose contents depend on contacts rather than
      // on the messages themselves — un-flagging a contact changes which senders belong here.
      // Post-filter on `vip` so unrelated contact edits (a renamed title, a new phone) don't reload.
      this.mailAppStateService.getVirtualFolder().pipe(
        switchMap((folder) =>
          folder === 'vip'
            ? this.stitchEventsService
                .getEvents(() => this.stitchSignalSpaceIds(), StitchType.CONTACT, SignalStitchEventType.UPDATED)
                .pipe(filter((event) => !!event.changes && 'vip' in event.changes))
            : EMPTY,
        ),
      ),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });

    // An email opened from the search popup's "Open email" is selected here as a click would select it, if this list
    // shows it; otherwise nothing stays selected, so no row reads as the one in the reader (searchopen r2). Only once
    // it IS in the reader: a failed open leaves the selection with the email still on screen (Codex r4 P2).
    this.readerOpen?.opened$
      .pipe(
        filter(({ origin }) => origin === 'search'),
        takeUntil(this.alive),
      )
      .subscribe(({ id }) => this.selectOnly(id));
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes) {
      this.effectiveThreadedView = MessagesFilters.isThreadedViewEffective(this.filters);
    }
    if (
      (changes.filters && !changes.filters.isFirstChange()) ||
      (changes.tenantItems &&
        !changes.tenantItems.isFirstChange() &&
        this.tenantItemsKey(changes.tenantItems.previousValue) !== this.tenantItemsKey(changes.tenantItems.currentValue))
    ) {
      this.resetItems();
    }
  }

  /**
   * A filter, search or sort change starts a different list, so the last list's cut-short line goes with its rows
   * rather than standing over the new search while it loads — or for good, since a failed load answers nothing.
   * A page change of the same search goes through `loadPage` and keeps the line until that page answers.
   */
  public override resetItems(): void {
    this.truncated.set(false);
    super.resetItems();
  }

  private tenantItemsKey(items: ReturnType<MessagesListComponent['tenantItems']>): string {
    return JSON.stringify(items?.map(({ id, accountId }) => [id, accountId]));
  }

  protected override getItems(
    offset: number,
    limit: number,
  ): Observable<{ items: MessageSummaryDto[]; count: number; truncated?: true }> {
    return this.messagesService.search(
      {
        ...this.filters,
        archived: this.filters?.archived ?? false,
        deleted: this.filters?.deleted ?? false,
        limit,
        offset,
        // we don't need the full message body here
        // only a few bits of data to render the list
        short: true,
      },
      {
        withTags: this.withTags,
        withKnots: this.withKnots,
        withConnections: this.withConnections,
        knotsSource: this.knotsSource,
        tenantItems: this.tenantItems(),
      },
    ).pipe(tap(({ truncated }) => this.truncated.set(truncated === true)));
  }

  /**
   * Opens compose editor for draft messages on double-click.
   * Scheduled messages intentionally open in read-only view (not compose) —
   * following Gmail behavior where users must unschedule before editing.
   */
  public handleDoubleClickItem(message?: MessageSummaryDto): void {
    if (message.state === MessageState.DRAFT) {
      this.messagesService.composeMessage({ message, injector: this.injector });
    } else {
      this.doubleClickMessage.emit(message);
    }
  }

  /**
   * Opens the message the selection settled on in the main view.
   *
   * Drafts open the compose editor, but only for a pointer gesture. Arrowing through the
   * Drafts folder would otherwise spawn a compose popup that takes focus off the list and
   * stops further keyboard navigation, so the keyboard only moves the highlight there; a
   * click or a double-click still opens the draft. The same holds for a `removal` open, the
   * selection moving on after the selected draft was trashed: the next draft is highlighted,
   * not opened for editing.
   *
   * Scheduled messages open in read-only view with a "Cancel Schedule" bar —
   * following Gmail behavior where users must unschedule before editing.
   */
  /**
   * A click names the message at once, while the open waits out the double-click threshold, so the reader can
   * start reading it and say it is coming instead of sitting unchanged. Pointer only: arrowing through the list
   * would read every row passed over. A draft opens the compose, not the reader. Any open this list does not name
   * still replaces the one before it, so it calls that one off (`null`) rather than leave it pending for good.
   */
  public handleOpenPending(event: SelectableOpenEvent<MessageSummaryDto> | null): void {
    const named = event?.source === 'pointer' && event.item.data.state !== MessageState.DRAFT;
    this.pendingMessage.emit(named ? event.item.data : null);
  }

  public handleOpenSelected(event: SelectableOpenEvent<MessageSummaryDto>): void {
    const message = event.item.data;

    if (message.state === MessageState.DRAFT) {
      if (event.source === 'pointer') {
        this.messagesService.composeMessage({ message, injector: this.injector });
      }
      return;
    }

    this.clickMessage.emit(message);
  }

  public handleThreadMessageClick(threadMessage: MessageSummaryDto): void {
    this.clickThreadMessage.emit(threadMessage);
  }

  public loadThreadMessages(messageComponent: MessageComponent, reload = false): void {
    let offset = messageComponent.threadMessagesOffset;
    let limit = MessageComponent.threadMessagesLimit;

    if (reload) {
      offset = 0;
      limit += messageComponent.threadMessagesOffset;
    }

    if (reload || !messageComponent.threadMessagesTill) {
      messageComponent.threadMessagesTill = new Date();
    }

    messageComponent.threadMessagesLoading.set(true);
    this.messagesService
      .getThreadMessages(messageComponent.item.id, offset, limit, messageComponent.threadMessagesTill)
      .subscribe({
      next: (response) => {
        if (reload) {
          messageComponent.threadMessages.set(response.items);
        } else {
          messageComponent.threadMessages.update((threadMessages) => {
            threadMessages.push(...response.items);
            return threadMessages;
          });
        }
        messageComponent.threadMessagesCount.set(response.count);
        messageComponent.threadMessagesLoading.set(false);
      },
      error: () => {
        messageComponent.threadMessagesLoading.set(false);
      },
    });
  }

  public async reply(message: MessageSummaryDto): Promise<void> {
    this.messagesService.composeMessage({
      reply: message,
      action: 'reply',
      injector: this.injector,
    });
  }

  public async forward(message: MessageSummaryDto): Promise<void> {
    this.messagesService.composeMessage({
      forward: [message],
      action: 'forward',
      injector: this.injector,
    });
  }
}
